/**
 * SoleadLab 2.0 — Dock Mission Adapter (F-03E-1)
 *
 * Pure functions translating between the existing frontend
 * `DemoMission` / `DemoWaypoint` / `DemoDockStation` shapes and the
 * backend F-03 `dock_missions` row + the F-03D-1 / F-03D-3 export
 * payload contract. F-03E-1 ships **only** the adapter — no React, no
 * DOM, no store imports, no `apiFetch`, no localStorage. The adapter is
 * the single source of truth for "what `plan_json` looks like" until
 * F-03E-4 wires the export button.
 *
 * Phase 1 deliberate limitation around leg semantics:
 *   The frontend demo model carries `legParams` on individual waypoints
 *   that describe the LEG LEAVING that waypoint (gimbal carry-over,
 *   record-video start/stop transitions, distance-based interval
 *   photo). DJI WPML's actual start/stop semantics require comparing
 *   ADJACENT waypoints to detect the transition edge — a single-
 *   waypoint adapter cannot determine when recording actually starts vs
 *   continues without inspecting the whole sequence. F-03E-1 therefore:
 *
 *     - Maps explicit waypoint-level fields (`alt → altM`, `heading →
 *       headingDeg`, `gimbalPitch → gimbalPitchDeg`, `gimbalYaw →
 *       gimbalYawDeg`, `speedMps → speedMps`) into the F-03D-1 Waypoint
 *       contract.
 *     - Maps `wp.action === 'photo'` (with `photoPayload`) into a
 *       `{ type: 'takePhoto', lenses }` action — unambiguous.
 *     - Maps `wp.action === 'hover'` (with `hoverSec`) into a
 *       `{ type: 'hover', durationSec }` action — unambiguous.
 *     - PRESERVES every waypoint's full `legParams` under
 *       `plan_json.legParamsByWaypoint[]` (positional array) so a
 *       future F-03E-4 unit can do a full sequence-aware adapter pass
 *       without us silently inventing `startRecord` / `stopRecord` /
 *       interval `takePhoto` actions here.
 *
 *   This limitation is documented in the test file as well so a future
 *   reader sees the boundary clearly.
 */

import type {
  CompatibilityProfileVersion,
  DockMissionPlanJson,
  DockMissionRow,
  CreateDockMissionPayload,
  UpdateDockMissionPayload,
  TargetAircraftModel,
  TargetDockModel,
} from './dockApi';
import type {
  DemoDockStation,
  DemoHeadingMode,
  DemoLegParams,
  DemoMission,
  DemoMissionFinishAction,
  DemoPhotoPayload,
  DemoWaypoint,
  SavedDockSnapshot,
} from '../components/dock-demo/types';

// ─── Adapter error model ───────────────────────────────────────────────────

export type DockMissionAdapterErrorCode =
  | 'UNSUPPORTED_DOCK_AIRCRAFT_PAIRING'
  | 'UNSUPPORTED_DRONE_MODEL'
  | 'EMPTY_WAYPOINTS'
  | 'INVALID_WAYPOINT'
  | 'INVALID_MISSION_NAME'
  | 'INVALID_BACKEND_ROW';

export class DockMissionAdapterError extends Error {
  readonly code: DockMissionAdapterErrorCode;
  readonly details?: Record<string, unknown>;

  constructor(
    code: DockMissionAdapterErrorCode,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'DockMissionAdapterError';
    this.code = code;
    this.details = details;
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

// ─── Dock × aircraft pairing ───────────────────────────────────────────────

/**
 * Single source of truth for the Phase-1 pairing rule. Mirrors the
 * backend `DOCK_AIRCRAFT_PAIRINGS` map in
 * `soleadlab-api/src/services/wpmlExport/profiles.ts` AND the DB-level
 * CHECK constraint from F-03B migration `0042_dock_missions.sql`. Any
 * future hardware addition MUST update all three locations.
 */
const DOCK_AIRCRAFT_PAIRINGS: Record<TargetDockModel, readonly TargetAircraftModel[]> = {
  dock2: ['matrice_3d', 'matrice_3td'],
  dock3: ['matrice_4d', 'matrice_4td'],
};

export function isValidDockAircraftPairing(
  dock: TargetDockModel,
  aircraft: TargetAircraftModel,
): boolean {
  return DOCK_AIRCRAFT_PAIRINGS[dock].includes(aircraft);
}

/**
 * Phase-1 default aircraft for each dock. F-03A diagnosis decision #8
 * selected the thermal-capable inspection variant (`matrice_3td` for
 * Dock 2, `matrice_4td` for Dock 3) — these are the variants the
 * platform's IR/thermal tooling targets by default.
 */
export function getDefaultAircraftForDock(dock: TargetDockModel): TargetAircraftModel {
  return dock === 'dock2' ? 'matrice_3td' : 'matrice_4td';
}

/**
 * If the frontend currently knows the dock station's aircraft via
 * `DemoDockStation.drone.model` (DJI marketing shorthand: `M3D` /
 * `M3TD` / `M30T`), translate to the F-03 enum. `M30T` is intentionally
 * unsupported in Phase 1 — the F-03B DB CHECK does not allow it and
 * F-03D-1 does not export it. F-03E-1 fails fast rather than silently
 * coercing.
 */
export function droneModelToTargetAircraft(model: 'M3D' | 'M3TD' | 'M30T'): TargetAircraftModel {
  if (model === 'M3D') return 'matrice_3d';
  if (model === 'M3TD') return 'matrice_3td';
  throw new DockMissionAdapterError(
    'UNSUPPORTED_DRONE_MODEL',
    `Drone model '${model}' is not supported by F-03 Phase 1. Supported: M3D, M3TD (Dock 2). Dock 3 / Matrice 4D / 4TD planning is supported via UI dock selection but cannot be inferred from this legacy field.`,
    { droneModel: model },
  );
}

// ─── Waypoint mapping ──────────────────────────────────────────────────────

/**
 * One F-03D-1 waypoint action — discriminated union mirroring
 * `soleadlab-api/src/services/wpmlExport/types.ts:WaypointAction`. F-03E-1
 * emits only the two unambiguous variants from the demo `wp.action`
 * field: `takePhoto` and `hover`. `gimbalRotate`, `startRecord`,
 * `stopRecord` belong to the sequence-aware leg pass (deferred — see
 * file header).
 */
type ExportWaypointAction =
  | { type: 'takePhoto'; lenses: ReadonlyArray<DemoPhotoPayload>; fileSuffix?: string }
  | { type: 'hover'; durationSec: number };

interface ExportWaypoint {
  lng: number;
  lat: number;
  altM: number;
  speedMps?: number;
  headingDeg?: number;
  gimbalPitchDeg?: number;
  gimbalYawDeg?: number;
  actions?: ExportWaypointAction[];
}

function assertFiniteCoordinate(value: number, field: string, waypointIndex: number): void {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new DockMissionAdapterError(
      'INVALID_WAYPOINT',
      `Waypoint at index ${waypointIndex} has invalid ${field}`,
      { waypointIndex, field, value },
    );
  }
}

function mapWaypoint(wp: DemoWaypoint, index: number): ExportWaypoint {
  assertFiniteCoordinate(wp.lng, 'lng', index);
  assertFiniteCoordinate(wp.lat, 'lat', index);
  assertFiniteCoordinate(wp.alt, 'alt', index);

  const out: ExportWaypoint = {
    lng: wp.lng,
    lat: wp.lat,
    altM: wp.alt,
  };
  if (wp.speedMps !== undefined) out.speedMps = wp.speedMps;
  if (wp.heading !== undefined) out.headingDeg = wp.heading;
  if (wp.gimbalPitch !== undefined) out.gimbalPitchDeg = wp.gimbalPitch;
  if (wp.gimbalYaw !== undefined) out.gimbalYawDeg = wp.gimbalYaw;

  const actions: ExportWaypointAction[] = [];
  if (wp.action === 'photo') {
    const lenses = (wp.photoPayload && wp.photoPayload.length > 0
      ? wp.photoPayload
      : (['wide'] as DemoPhotoPayload[]));
    actions.push({
      type: 'takePhoto',
      lenses,
      fileSuffix: `WP${index}_${lenses.join('-')}`,
    });
  } else if (wp.action === 'hover') {
    actions.push({
      type: 'hover',
      durationSec: wp.hoverSec ?? 2,
    });
  }
  if (actions.length > 0) out.actions = actions;
  return out;
}

// ─── Mission name + general validation ─────────────────────────────────────

function normalizeMissionName(raw: string | undefined | null): string {
  if (typeof raw !== 'string') {
    throw new DockMissionAdapterError(
      'INVALID_MISSION_NAME',
      'Mission name is required',
    );
  }
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    throw new DockMissionAdapterError(
      'INVALID_MISSION_NAME',
      'Mission name must not be empty or whitespace-only',
    );
  }
  return trimmed;
}

// ─── Aircraft + dock resolution ────────────────────────────────────────────

function resolveTargetDockModel(mission: DemoMission): TargetDockModel {
  return mission.targetDockModel ?? 'dock2';
}

function resolveTargetAircraftModel(
  mission: DemoMission,
  dock: TargetDockModel,
): TargetAircraftModel {
  const candidate = mission.targetAircraftModel;
  if (candidate && isValidDockAircraftPairing(dock, candidate)) {
    return candidate;
  }
  // Either no aircraft was set OR the stored aircraft no longer matches
  // the (possibly updated) dock — fall back to the dock's default.
  const fallback = getDefaultAircraftForDock(dock);
  if (candidate && !isValidDockAircraftPairing(dock, candidate)) {
    // If the caller explicitly set an invalid aircraft for the dock,
    // surface as a pairing failure rather than silently re-default.
    throw new DockMissionAdapterError(
      'UNSUPPORTED_DOCK_AIRCRAFT_PAIRING',
      `Aircraft '${candidate}' is not valid for dock '${dock}'. Supported pairings: dock2 + matrice_3d/matrice_3td, dock3 + matrice_4d/matrice_4td.`,
      { targetDockModel: dock, targetAircraftModel: candidate },
    );
  }
  return fallback;
}

function resolveCompatibilityProfileVersion(
  mission: DemoMission,
): CompatibilityProfileVersion {
  const v = mission.compatibilityProfileVersion;
  return v === 'v1' || v === undefined ? 'v1' : 'v1';
}

// ─── plan_json builder ─────────────────────────────────────────────────────

interface DockSnapshot {
  dockStationId: string;
  lng: number;
  lat: number;
  elevationM?: number;
  geofence?:
    | { type: 'radius'; radiusM: number }
    | { type: 'polygon'; coordinates: ReadonlyArray<readonly [number, number]> };
  noFlyZones?: ReadonlyArray<{
    id: string;
    name?: string;
    vertices: ReadonlyArray<readonly [number, number]>;
    bufferM?: number;
  }>;
  defaults?: {
    rthAltM: number;
    maxAltM: number;
    maxSpeedMps: number;
    maxDistanceM: number;
  };
}

interface UiDefaults {
  defaultAlt?: number;
  defaultGimbalPitch?: number;
  defaultLegParams?: DemoLegParams;
}

interface LegParamsEntry {
  waypointIndex: number;
  legParams: DemoLegParams;
}

/**
 * Mission-level + per-waypoint planJson payload. Backend treats this as
 * opaque (only structural gates are JSON-roundtripability + 256 KB
 * size cap). F-03D-1 export reads the F-03D-1 contract fields only;
 * extra fields (`dockSnapshot`, `uiDefaults`, `legParamsByWaypoint`)
 * are preserved verbatim so a future F-03E-4 unit + future server-side
 * geofence validation can consume them.
 */
interface BuiltPlanJson extends DockMissionPlanJson {
  waypoints: ExportWaypoint[];
  globalSpeedMps?: number;
  finishAction?: DemoMissionFinishAction;
  headingMode?: DemoHeadingMode;
  coordinateMode?: 'WGS84';
  heightMode?: 'relativeToStartPoint' | 'EGM96';
  dockSnapshot?: DockSnapshot;
  uiDefaults?: UiDefaults;
  legParamsByWaypoint?: LegParamsEntry[];
}

export function buildDockMissionPlanJson(
  mission: DemoMission,
  activeDock?: DemoDockStation | null,
): BuiltPlanJson {
  if (!Array.isArray(mission.waypoints) || mission.waypoints.length === 0) {
    throw new DockMissionAdapterError(
      'EMPTY_WAYPOINTS',
      'Mission must contain at least one waypoint before export/save',
    );
  }

  const waypoints = mission.waypoints.map((wp, idx) => mapWaypoint(wp, idx));
  const plan: BuiltPlanJson = { waypoints };

  if (mission.globalSpeedMps !== undefined) plan.globalSpeedMps = mission.globalSpeedMps;
  if (mission.finishAction !== undefined) plan.finishAction = mission.finishAction;
  if (mission.headingMode !== undefined) plan.headingMode = mission.headingMode;
  // F-03D-1 hardcodes WGS84 + relativeToStartPoint when these fields are
  // absent from the input. F-03E-1 only emits them when the demo model
  // carries an explicit override (none today, but future-proofed here).
  plan.coordinateMode = 'WGS84';
  plan.heightMode = 'relativeToStartPoint';

  // Optional snapshot of the dock the mission is bound to. We carry just
  // enough for a future server-side geofence / NFZ validation pass — no
  // operator PII, no status / health, no firmware.
  if (activeDock && activeDock.id) {
    const snap: DockSnapshot = {
      dockStationId: activeDock.id,
      lng: activeDock.lng,
      lat: activeDock.lat,
    };
    if (activeDock.elevation !== undefined) snap.elevationM = activeDock.elevation;
    if (activeDock.geofence) snap.geofence = activeDock.geofence;
    if (activeDock.noFlyZones && activeDock.noFlyZones.length > 0) {
      snap.noFlyZones = activeDock.noFlyZones.map((nfz) => {
        const out: DockSnapshot['noFlyZones'] extends infer T
          ? T extends ReadonlyArray<infer U>
            ? U
            : never
          : never = {
          id: nfz.id,
          vertices: nfz.vertices,
        };
        if (nfz.name !== undefined) out.name = nfz.name;
        if (nfz.bufferM !== undefined) out.bufferM = nfz.bufferM;
        return out;
      });
    }
    if (activeDock.defaults) snap.defaults = activeDock.defaults;
    plan.dockSnapshot = snap;
  }

  // Optional UI defaults — survive across devices for the same mission
  // (these are F-03E placement defaults, not flight-affecting fields).
  const uiDefaults: UiDefaults = {};
  if (mission.defaultAlt !== undefined) uiDefaults.defaultAlt = mission.defaultAlt;
  if (mission.defaultGimbalPitch !== undefined) {
    uiDefaults.defaultGimbalPitch = mission.defaultGimbalPitch;
  }
  if (mission.defaultLegParams !== undefined) {
    uiDefaults.defaultLegParams = mission.defaultLegParams;
  }
  if (Object.keys(uiDefaults).length > 0) plan.uiDefaults = uiDefaults;

  // Preserve per-waypoint legParams VERBATIM for a future sequence-aware
  // F-03E-4 export pass. See file header for the rationale on why we do
  // NOT synthesize start/stop record actions here.
  const legParams: LegParamsEntry[] = [];
  mission.waypoints.forEach((wp, idx) => {
    if (wp.legParams) legParams.push({ waypointIndex: idx, legParams: wp.legParams });
  });
  if (legParams.length > 0) plan.legParamsByWaypoint = legParams;

  return plan;
}

// ─── Public payload builders ───────────────────────────────────────────────

export function demoMissionToCreatePayload(
  mission: DemoMission,
  activeDock?: DemoDockStation | null,
): CreateDockMissionPayload {
  const name = normalizeMissionName(mission.name);
  const targetDockModel = resolveTargetDockModel(mission);
  const targetAircraftModel = resolveTargetAircraftModel(mission, targetDockModel);
  const compatibilityProfileVersion = resolveCompatibilityProfileVersion(mission);
  const planJson = buildDockMissionPlanJson(mission, activeDock);

  const payload: CreateDockMissionPayload = {
    name,
    planJson,
    targetDockModel,
    targetAircraftModel,
    compatibilityProfileVersion,
  };
  // Description: undefined → omit; explicit `null` → preserve null (the
  // backend Zod schema accepts `null` to clear). Whitespace-only strings
  // are trimmed and treated as "no description provided".
  if (mission.description !== undefined) {
    const trimmed =
      typeof mission.description === 'string' ? mission.description.trim() : '';
    payload.description = trimmed.length > 0 ? trimmed : null;
  }
  return payload;
}

/**
 * Build the PATCH body. F-03E-1 ships the simplest correct variant: send
 * the full payload every save, so the backend's "≥1 field present"
 * refinement is always satisfied and tests stay deterministic. A future
 * unit can switch to a diff-only patch once the store carries the
 * pre-save snapshot. Backend ignores fields it doesn't know about, and
 * the dock × aircraft pairing is re-validated against the merged state
 * server-side, so this is safe.
 */
export function demoMissionToUpdatePayload(
  mission: DemoMission,
  activeDock?: DemoDockStation | null,
): UpdateDockMissionPayload {
  const created = demoMissionToCreatePayload(mission, activeDock);
  return {
    name: created.name,
    description: created.description,
    planJson: created.planJson,
    targetDockModel: created.targetDockModel,
    targetAircraftModel: created.targetAircraftModel,
    compatibilityProfileVersion: created.compatibilityProfileVersion,
  };
}

// ─── Backend row → DemoMission ─────────────────────────────────────────────

/**
 * Best-effort reverse adapter — keeps the frontend `DemoMission` shape
 * stable while wiring server-side reads in F-03E-2. The reverse path is
 * lossy for the F-03D-1-only fields (`coordinateMode`, `heightMode`,
 * synthesized `gimbalRotate` / `startRecord` actions) but is sufficient
 * to populate the planner UI from a persisted mission.
 */
export function backendRowToDemoMission(row: DockMissionRow): DemoMission {
  if (!row || typeof row.id !== 'string' || typeof row.name !== 'string') {
    throw new DockMissionAdapterError(
      'INVALID_BACKEND_ROW',
      'Backend dock mission row is malformed (missing id / name)',
      { row: row as unknown },
    );
  }
  const plan = (row.plan_json ?? {}) as BuiltPlanJson;
  const exportWaypoints = Array.isArray(plan.waypoints) ? plan.waypoints : [];
  const legParamsByIndex = new Map<number, DemoLegParams>();
  if (Array.isArray(plan.legParamsByWaypoint)) {
    for (const entry of plan.legParamsByWaypoint) {
      if (
        entry
        && typeof entry.waypointIndex === 'number'
        && entry.legParams
      ) {
        legParamsByIndex.set(entry.waypointIndex, entry.legParams);
      }
    }
  }

  const waypoints: DemoWaypoint[] = exportWaypoints.map((wp, idx) => {
    const photoAction = wp.actions?.find((a) => a.type === 'takePhoto');
    const hoverAction = wp.actions?.find((a) => a.type === 'hover');
    const out: DemoWaypoint = {
      id: `${row.id}-wp-${idx}`,
      lng: wp.lng,
      lat: wp.lat,
      alt: wp.altM,
      action: photoAction ? 'photo' : hoverAction ? 'hover' : 'none',
    };
    if (wp.speedMps !== undefined) out.speedMps = wp.speedMps;
    if (wp.headingDeg !== undefined) out.heading = wp.headingDeg;
    if (wp.gimbalPitchDeg !== undefined) out.gimbalPitch = wp.gimbalPitchDeg;
    if (wp.gimbalYawDeg !== undefined) out.gimbalYaw = wp.gimbalYawDeg;
    if (photoAction && photoAction.type === 'takePhoto') {
      out.photoPayload = [...photoAction.lenses];
    }
    if (hoverAction && hoverAction.type === 'hover') {
      out.hoverSec = hoverAction.durationSec;
    }
    const lp = legParamsByIndex.get(idx);
    if (lp) out.legParams = lp;
    return out;
  });

  const mission: DemoMission = {
    id: row.id,
    name: row.name,
    description: row.description ?? undefined,
    waypoints,
    globalSpeedMps: plan.globalSpeedMps ?? 5,
    finishAction: plan.finishAction ?? 'goHome',
    headingMode: plan.headingMode ?? 'followWayline',
    targetDockModel: row.target_dock_model,
    targetAircraftModel: row.target_aircraft_model,
    compatibilityProfileVersion: 'v1',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
  if (plan.dockSnapshot && typeof plan.dockSnapshot === 'object') {
    mission.dockStationId = plan.dockSnapshot.dockStationId;
    // F-03J-1: surface the persisted dock snapshot on the in-memory
    // mission so the planner panel can fall back to it for Export when
    // the live `DemoDockStation` entity is missing on this device
    // (different browser / cleared localStorage / fresh profile). The
    // snapshot's coordinates/geofence/NFZs/defaults are exactly what the
    // F-03H WPML generator already consumes for `takeOffRefPoint` and
    // `globalRTHHeight` precedence, so the UI gate and the backend
    // emission stay aligned. NEVER round-tripped back into plan_json on
    // save — the create/update payload builders read live `activeDock`
    // and re-build a fresh snapshot from it.
    const snap = plan.dockSnapshot;
    if (typeof snap.dockStationId === 'string'
      && typeof snap.lng === 'number'
      && typeof snap.lat === 'number'
    ) {
      const saved: SavedDockSnapshot = {
        dockStationId: snap.dockStationId,
        lng: snap.lng,
        lat: snap.lat,
      };
      if (typeof snap.elevationM === 'number') saved.elevationM = snap.elevationM;
      if (snap.geofence) {
        // Coerce ReadonlyArray<readonly [..]> in the persisted shape into
        // the mutable [..][] shape SavedDockSnapshot exposes. Validation
        // only reads from it; no callers mutate.
        if (snap.geofence.type === 'radius') {
          saved.geofence = { type: 'radius', radiusM: snap.geofence.radiusM };
        } else {
          saved.geofence = {
            type: 'polygon',
            coordinates: snap.geofence.coordinates.map((c) => [c[0], c[1]] as [number, number]),
          };
        }
      }
      if (snap.noFlyZones && snap.noFlyZones.length > 0) {
        saved.noFlyZones = snap.noFlyZones.map((nfz) => {
          const out: NonNullable<SavedDockSnapshot['noFlyZones']>[number] = {
            id: nfz.id,
            vertices: nfz.vertices.map((v) => [v[0], v[1]] as [number, number]),
          };
          if (nfz.name !== undefined) out.name = nfz.name;
          if (nfz.bufferM !== undefined) out.bufferM = nfz.bufferM;
          return out;
        });
      }
      if (snap.defaults) {
        saved.defaults = {
          rthAltM: snap.defaults.rthAltM,
          maxAltM: snap.defaults.maxAltM,
          maxSpeedMps: snap.defaults.maxSpeedMps,
          maxDistanceM: snap.defaults.maxDistanceM,
        };
      }
      mission.savedDockSnapshot = saved;
    }
  }
  if (plan.uiDefaults && typeof plan.uiDefaults === 'object') {
    if (plan.uiDefaults.defaultAlt !== undefined) mission.defaultAlt = plan.uiDefaults.defaultAlt;
    if (plan.uiDefaults.defaultGimbalPitch !== undefined) {
      mission.defaultGimbalPitch = plan.uiDefaults.defaultGimbalPitch;
    }
    if (plan.uiDefaults.defaultLegParams !== undefined) {
      mission.defaultLegParams = plan.uiDefaults.defaultLegParams;
    }
  }
  return mission;
}
